import {
  computed,
  ref,
  toRef,
} from "vue";

import AHttpAPI from "aloha-vue/src/compositionAPI/AHttpAPI";
import ANotificationAPI from "aloha-vue/src/compositionAPI/ANotificationAPI";
import FiltersCombineCopiesAPI from "./FiltersCombineCopiesAPI";
import UtilsAPI from "./UtilsAPI";

import PuxTablePlugin from "../plugin/PuxTablePlugin";

import moment from "moment/moment";
import EventBus from "aloha-vue/src/utils/EventBus";
import {
  setMainAndAppliedModel,
} from "../utils/utils";
import {
  cloneDeep,
  concat,
  find,
  findIndex,
  forEach,
  groupBy,
  isArray,
  isFunction,
  isNil,
  isObject,
  isPlainObject,
  keyBy,
  values,
} from "lodash-es";

const FILTER_TYPE_MAPPING = {
  daterange: "dateRange",
  numberrange: "numberRange",
};

export default function FiltersAPI(props, { emit }, {
  appliedModelFilters = ref({}),
  filterPrioritization = ref(undefined), // 1) Url 2) Session 3) Default
  initSorting = () => {},
  mainModelFilters = ref({}),
  offsetLocal = ref(0),
  puxTableRef = ref(undefined),
  reloadData = () => {},
  sortingFromColumns = ref(undefined),
  unappliedModelFilters = ref({}),
}) {
  const filters = toRef(props, "filters");
  const id = toRef(props, "id");
  const isSimpleTable = toRef(props, "isSimpleTable");
  const updateAppliedModelFilters = toRef(props, "updateAppliedModelFilters");
  const updateUnappliedModelFilters = toRef(props, "updateUnappliedModelFilters");
  const urlUpdate = toRef(props, "urlUpdate");
  const viewFilters = toRef(props, "viewFilters");

  const eventBussesForFilter = ref({});
  const excludeRenderAttributesForFilters = [
    "alwaysVisible",
    "group",
    "permission",
    "main",
    "default",
    "modelId",
    "permissionMissing",
    "event",
  ];
  const filterObjectLocal = ref({});
  const filtersPropsAndFromAPI = ref([]);
  const filtersRef = ref(undefined);
  const filtersVisible = ref(true);
  const readyFilters = ref(false);
  const timerUpdate = ref(undefined);

  const {
    changeSnaKeCaseToCamelCase,
    checkPermissionAndContextType,
  } = UtilsAPI();

  const {
    getListHttp,
  } = AHttpAPI();

  const {
    checkFilterModelForCombineCopies,
    onCombineCopiesData,
  } = FiltersCombineCopiesAPI();

  const {
    addNotification,
  } = ANotificationAPI();

  const {
    isAuthenticated,
    user,
  } = PuxTablePlugin.UserAPI();

  const isFiltersViewRight = computed(() => {
    return viewFilters.value === "right";
  });

  const updateModelFilterForType = ({ filter, currentModel }) => {
    if ((filter.type === "multiselect"
      || filter.type === "checkbox"
      || filter.type === "radio") && !isArray(currentModel)) {
      return [currentModel];
    }
    if (filter.type === "switch") {
      const TRUE_VALUE = filter.trueValue || true;
      if (currentModel === TRUE_VALUE || RegExp(/true/i).test(`${ currentModel }`)) {
        return TRUE_VALUE;
      }
      const FALSE_VALUE = filter.falseValue || false;
      if (currentModel === FALSE_VALUE || RegExp(/false/i).test(`${ currentModel }`)) {
        return FALSE_VALUE;
      }
    }
    if (isObject(currentModel)) {
      return cloneDeep(currentModel);
    }
    return currentModel;
  };

  const showFiltersRightParent = () => {
    if (isFiltersViewRight.value &&
      filtersVisible.value) {
      emit("showFiltersRight");
    }
  };

  const updateFiltersVisible = configForCurrentTable => {
    if (configForCurrentTable?.tcg_rest?.hideFilters) {
      filtersVisible.value = false;
    }
    showFiltersRightParent();
  };

  const setParameterDeselectForAllSelectFilters = filters => {
    forEach(filters, filter => {
      if (filter.type === "select") {
        filter.deselectable = true;
      }
    });
    return filters;
  };

  const updateFiltersFromAPI = configForCurrentTable => {
    const FILTERS_FROM_API = configForCurrentTable?.filters || [];
    const FILTERS_OBJ = {};
    forEach(concat(cloneDeep(filters.value), FILTERS_FROM_API), filter => {
      const FILTER_CAMEL_CASE = changeSnaKeCaseToCamelCase({ obj: filter, prefix: "tcf" });

      if (!checkPermissionAndContextType(FILTER_CAMEL_CASE)) {
        return;
      }
      FILTERS_OBJ[FILTER_CAMEL_CASE.id] = FILTER_CAMEL_CASE;
    });
    filtersPropsAndFromAPI.value = setParameterDeselectForAllSelectFilters(values(FILTERS_OBJ));
  };

  const filtersLocal = computed(() => {
    const FILTERS = cloneDeep(filtersPropsAndFromAPI.value);
    forEach(FILTERS, filter => {
      const FILTER_ID = filter.id;
      filter.type = FILTER_TYPE_MAPPING[filter.type] || filter.type;
      if (filterObjectLocal.value[FILTER_ID]) {
        filter = Object.assign(filter, filterObjectLocal.value[FILTER_ID]);
      }
      if (filter.children && filter.children.length) {
        forEach(filter.children, filterChild => {
          if (filterObjectLocal.value[filterChild.id]) {
            filterChild = Object.assign(filterChild, filterObjectLocal.value[filterChild.id]);
          }
        });
      }
      if (filter.type === "dateRange") {
        filter.placeholderFrom = filter.placeholderFrom || filter.placeholderMin;
        filter.placeholderUntil = filter.placeholderUntil || filter.placeholderMax;
        filter.placeholderMin = undefined;
        filter.placeholderMax = undefined;
        filter.keyFrom = `${ filter.id }_after`;
        filter.keyUntil = `${ filter.id }_before`;
      } else if (filter.type === "numberRange") {
        filter.keyMin = `${ filter.id }_min`;
        filter.keyMax = `${ filter.id }_max`;
      }
      if (filter.searchApi) {
        filter.search = false;
      }
      if (filter.labelScreenreader) {
        filter.labelScreenReader = filter.labelScreenreader;
        filter.labelScreenreader = undefined;
      }
      // Barrierefreiheit
      filter.tcfId = undefined;
      filter.defaultJson = undefined;
      filter.tableConfig = undefined;
      filter.dataExtra = filter.dataExtra || undefined;
      filter.combineCopies = undefined;
      if (!filter.searchApi) {
        filter.url = undefined;
      }
    });
    return FILTERS;
  });

  const filtersLocalGrouped = computed(() => {
    const FILTERS = {
      main: undefined,
      rest: [],
    };

    forEach(filtersLocal.value, filter => {
      if (filter.main) {
        FILTERS.main = cloneDeep(filter);
        FILTERS.main.main = undefined;
        FILTERS.main.isLabelFloat = true;
        if (FILTERS.main.type === "integer") {
          FILTERS.main.controlsType = "none";
        }
      } else {
        FILTERS.rest.push(filter);
      }
    });

    return FILTERS;
  });

  const filtersKeyById = computed(() => {
    return keyBy(filtersLocal.value, "id");
  });

  const initFilterModelFromUrl = ({ urlParams }) => {
    const FILTER_MODEL = {};
    forEach(filtersPropsAndFromAPI.value, filter => {
      const MODEL_ID = filter.modelId || filter.id;
      if (urlParams[MODEL_ID]) {
        filterPrioritization.value = "Url";
        FILTER_MODEL[MODEL_ID] = updateModelFilterForType({ filter, currentModel: urlParams[MODEL_ID] });
      } else if (urlParams[`${ MODEL_ID }_after`] || urlParams[`${ MODEL_ID }_before`]) { // dateRange
        filterPrioritization.value = "Url";
        FILTER_MODEL[MODEL_ID] = FILTER_MODEL[MODEL_ID] || {};
        FILTER_MODEL[MODEL_ID][`${ MODEL_ID }_after`] = urlParams[`${ MODEL_ID }_after`] || undefined;
        FILTER_MODEL[MODEL_ID][`${ MODEL_ID }_before`] = urlParams[`${ MODEL_ID }_before`] || undefined;
      } else if (urlParams[`${ MODEL_ID }_min`] || urlParams[`${ MODEL_ID }_max`]) { // numberRange
        FILTER_MODEL[MODEL_ID] = FILTER_MODEL[MODEL_ID] || {};
        filterPrioritization.value = "Url";
        FILTER_MODEL[MODEL_ID][`${ MODEL_ID }_min`] = urlParams[`${ MODEL_ID }_min`] || undefined;
        FILTER_MODEL[MODEL_ID][`${ MODEL_ID }_max`] = urlParams[`${ MODEL_ID }_max`] || undefined;
      }
    });

    if (filterPrioritization.value === "Url") {
      const {
        applied,
        main,
      } = setMainAndAppliedModel({ filterMain: filtersLocalGrouped.value.main, model: FILTER_MODEL });

      mainModelFilters.value = main;
      appliedModelFilters.value = applied;
      unappliedModelFilters.value = cloneDeep(applied);
    }
  };

  const initFilterModelAndSortingFromLocalStorage = () => {
    if (!urlUpdate.value ||
      filterPrioritization.value === "Url" ||
      isSimpleTable.value) {
      return;
    }
    let filterLocalStorage = localStorage.getItem(`${ id.value }_filter`);
    if (!filterLocalStorage || filterLocalStorage === "undefined") {
      return;
    }
    try {
      filterLocalStorage = JSON.parse(filterLocalStorage);
    } catch (e) {
      return;
    }
    if (!filterLocalStorage) {
      return;
    }

    if (isAuthenticated.value) {
      if (filterLocalStorage._userPk !== user.value.pk) {
        localStorage.removeItem(`${ id.value }_filter`);
        return;
      }
    } else {
      if (filterLocalStorage._userPk !== "_anonymous") {
        localStorage.removeItem(`${ id.value }_filter`);
        return;
      }
    }

    const FILTER_MODEL = {};
    forEach(filtersPropsAndFromAPI.value, filter => {
      const MODEL_ID = filter.modelId || filter.id;
      if (filterLocalStorage[MODEL_ID]) {
        filterPrioritization.value = "LocalStorage";
        FILTER_MODEL[filter.id] = updateModelFilterForType({
          filter,
          currentModel: filterLocalStorage[MODEL_ID],
        });
      }
    });

    if (filterLocalStorage.ordering) { // Sortierung
      initSorting({ ordering: filterLocalStorage.ordering, filterPrioritization: "LocalStorage" });
    }

    if (filterPrioritization.value === "LocalStorage") {
      const {
        applied,
        main,
      } = setMainAndAppliedModel({ filterMain: filtersLocalGrouped.value.main, model: FILTER_MODEL });

      mainModelFilters.value = main;
      appliedModelFilters.value = applied;
      unappliedModelFilters.value = cloneDeep(applied);
    }
  };

  const setDefaultCurrentFilterModel = ({ filter }) => {
    let model = "";
    if (filter.default) {
      if (filter.type === "switch") {
        model = filter.default.toLowerCase() === "true";
      } else {
        model = cloneDeep(filter.default);
      }
    } else if (filter.defaultJson) {
      if (filter.type === "dateRange") {
        const TEMP_MODEL = {};
        forEach(filter.defaultJson, (item, key) => {
          if (item === "day" || item === "month" || item === "year") {
            TEMP_MODEL[key] = moment().startOf(item).format("YYYY-MM-DD");
          } else {
            TEMP_MODEL[key] = item;
          }
        });
        model = TEMP_MODEL;
      } else {
        model = cloneDeep(filter.defaultJson);
      }
    }
    return model;
  };

  const initFilterModelFromDefaultConfig = () => {
    if (filterPrioritization.value === "Url" ||
      filterPrioritization.value === "LocalStorage" ||
      isSimpleTable.value) {
      readyFilters.value = true;
      return;
    }
    const FILTER_MODEL = {};
    forEach(filtersPropsAndFromAPI.value, filter => {
      if (filter.default || filter.defaultJson) {
        const MODEL_ID = filter.modelId || filter.id;
        filterPrioritization.value = "Default";
        FILTER_MODEL[MODEL_ID] = setDefaultCurrentFilterModel({ filter });
      }
    });

    if (sortingFromColumns.value) { // Sortierung
      initSorting({ ordering: sortingFromColumns.value, filterPrioritization: "Default" });
    }

    if (filterPrioritization.value === "Default") {
      const {
        applied,
        main,
      } = setMainAndAppliedModel({ filterMain: filtersLocalGrouped.value.main, model: FILTER_MODEL });

      mainModelFilters.value = main;
      appliedModelFilters.value = applied;
      unappliedModelFilters.value = cloneDeep(applied);
    }
    readyFilters.value = true;
  };

  const updateMainModel = model => {
    mainModelFilters.value = cloneDeep(model);
  };

  const updateAppliedModelFiltersLocal = model => {
    clearTimeout(timerUpdate.value);
    if (isFunction(updateAppliedModelFilters.value)) {
      appliedModelFilters.value = updateAppliedModelFilters.value({ model });
    } else {
      appliedModelFilters.value = model;
    }
    offsetLocal.value = 0;
    if (puxTableRef.value && "offset" in puxTableRef.value) {
      puxTableRef.value.offset = 0;
    }

    if (isFiltersViewRight.value) {
      timerUpdate.value = setTimeout(() => {
        reloadData();
      }, 400);
    } else {
      reloadData();
    }
  };

  const updateUnappliedModelFiltersLocal = model => {
    if (isFunction(updateUnappliedModelFilters.value)) {
      unappliedModelFilters.value = updateUnappliedModelFilters.value({ model });
    } else {
      unappliedModelFilters.value = model;
    }
  };

  const showNotificationError = ({ error, errorKey }) => {
    const CURRENT_FILTER = find(filtersLocal.value, ["id", errorKey]) || {};
    let errorLocal = error;
    if (isArray(errorLocal)) {
      errorLocal = errorLocal.join(", ");
    }
    addNotification({ type: "error", text: `${ CURRENT_FILTER.label }: ${ errorLocal }` });
  };

  const changeModelFiltersLocalFromBadRequest = ({ errors }) => {
    let statusReload = false;
    const MODEL_FILTERS = cloneDeep(appliedModelFilters.value);
    if (isPlainObject(errors)) {
      forEach(errors, (error, errorKey) => {
        if (!isNil(MODEL_FILTERS[errorKey])) {
          MODEL_FILTERS[errorKey] = undefined;
          statusReload = true;
          showNotificationError({
            error,
            errorKey,
          });
        }
      });
    }
    if (statusReload) {
      unappliedModelFilters.value = cloneDeep(MODEL_FILTERS);
      updateAppliedModelFiltersLocal(MODEL_FILTERS);
    }
  };

  const checkFilterModel = ({ filter, list }) => {
    const FILTER_MODEL_ID = filter.id;
    let currentModelNew = undefined;
    let statusChangeModel = false;
    const CURRENT_MODEL = cloneDeep(appliedModelFilters.value[FILTER_MODEL_ID]);

    if (isArray(CURRENT_MODEL)) {
      if (CURRENT_MODEL.length) {
        currentModelNew = [];
        if (filter.combineCopies) {
          const tempObj = checkFilterModelForCombineCopies({ filter, list, CURRENT_MODEL });
          currentModelNew = tempObj.currentModelNew;
          statusChangeModel = tempObj.statusChangeModel;
        } else {
          forEach(CURRENT_MODEL, model => {
            if (findIndex(list, [filter.keyId, model]) !== -1) {
              currentModelNew.push(model);
            } else {
              statusChangeModel = true;
            }
          });
        }
      }
    } else if (!isNil(CURRENT_MODEL)) {
      if (filter.combineCopies) {
        const tempObj = checkFilterModelForCombineCopies({ filter, list, CURRENT_MODEL });
        currentModelNew = tempObj.currentModelNew;
        statusChangeModel = tempObj.statusChangeModel;
      } else {
        if (findIndex(list, [filter.keyId, CURRENT_MODEL]) !== -1) {
          currentModelNew = CURRENT_MODEL;
        } else {
          statusChangeModel = true;
        }
      }
    }

    if (statusChangeModel) {
      appliedModelFilters.value[FILTER_MODEL_ID] = currentModelNew;
      unappliedModelFilters.value[FILTER_MODEL_ID] = currentModelNew;
    }
  };

  // Hier reicht es nur für ein Filter prüfen, weil die Daten sollen gleich sein (modelId, keyId, combineCopies)
  const checkFilterModelWithModelId = ({ filters, listAll }) => {
    if (!filters?.length) {
      return;
    }

    const FILTER_MODEL_ID = filters?.[0].modelId;
    const CURRENT_MODEL = appliedModelFilters.value[FILTER_MODEL_ID];
    const currentModelNew = {};
    if (isArray(CURRENT_MODEL)) {
      if (CURRENT_MODEL.length) {
        forEach(filters, (filter, index) => {
          currentModelNew[filter.id] = [];
          const CURRENT_LIST = listAll[index];
          if (filter.combineCopies) {
            const tempObj = checkFilterModelForCombineCopies({ filter, list: CURRENT_LIST, CURRENT_MODEL });
            currentModelNew[filter.id] = tempObj.currentModelNew;
          } else {
            forEach(CURRENT_MODEL, model => {
              if (findIndex(CURRENT_LIST, [filter.keyId, model]) !== -1) {
                currentModelNew[filter.id].push(model);
              }
            });
          }
        });
      }
    } else if (!isNil(CURRENT_MODEL)) {
      forEach(filters, (filter, index) => {
        currentModelNew[filter.id] = [];
        const CURRENT_LIST = listAll[index];
        if (filter.combineCopies) {
          const tempObj = checkFilterModelForCombineCopies({ filter, list: CURRENT_LIST, CURRENT_MODEL });
          currentModelNew[filter.id] = tempObj.currentModelNew;
        } else {
          if (findIndex(CURRENT_LIST, [filter.keyId, CURRENT_MODEL]) !== -1) {
            currentModelNew[filter.id] = CURRENT_MODEL;
          }
        }
      });
    }

    appliedModelFilters.value = {
      ...appliedModelFilters.value,
      ...currentModelNew,
    };
    unappliedModelFilters.value = {
      ...unappliedModelFilters.value,
      ...currentModelNew,
    };
    delete appliedModelFilters.value[FILTER_MODEL_ID];
    delete unappliedModelFilters.value[FILTER_MODEL_ID];

    setTimeout(() => {
      filtersRef.value?.setFiltersVisibleIds();
    });
  };

  const loadDataForCurrentFilter = ({ filter }) => {
    const FILTER_ID = filter.id;
    if (filterObjectLocal.value[FILTER_ID] && filterObjectLocal.value[FILTER_ID].loading) {
      return;
    }
    filterObjectLocal.value[FILTER_ID] = filterObjectLocal.value[FILTER_ID] || {};
    filterObjectLocal.value[FILTER_ID].loading = true;
    const FILTER_URL_PARAMS = filter.urlParams || {};
    getListHttp({
      url: filter.url,
      urlParams: {
        // limit: 10000, TODO: PVP prüfen
        ...FILTER_URL_PARAMS,
      },
      apiSaveId: filter.apiSaveId,
    }).then(
      response => {
        let list;
        if (filter.combineCopies) {
          list = onCombineCopiesData({ list: response, filter });
        } else {
          list = response;
        }
        checkFilterModel({
          filter,
          list,
        });
        filterObjectLocal.value[FILTER_ID].data = list;
        filterObjectLocal.value[FILTER_ID].loading = false;
      }
    );
  };

  const updateDataForFilter = EVENT => {
    const FILTER = find(filtersPropsAndFromAPI.value, ["event", EVENT]);
    if (FILTER) {
      loadDataForCurrentFilter({ filter: FILTER });
    }
  };

  const setEventBusForFilterForUpdateData = filter => {
    const EVENT = filter.event;
    eventBussesForFilter.value[EVENT] = true;
    EventBus.$on(EVENT, () => updateDataForFilter(EVENT));
  };

  const loadDataForFiltersWithModelId = ({ filtersGroup }) => {
    const PROMISES = [];
    const FILTERS = [];
    forEach(filtersGroup, filter => {
      if (filter.url && !filter.searchApi && !filter.data) {
        const FILTER_ID = filter.id;
        if (filterObjectLocal.value[FILTER_ID] && filterObjectLocal.value[FILTER_ID].loading) {
          return;
        }

        filterObjectLocal.value[FILTER_ID] = filterObjectLocal.value[FILTER_ID] || {};
        filterObjectLocal.value[FILTER_ID].loading = true;

        const FILTER_URL_PARAMS = filter.urlParams || {};
        PROMISES.push(getListHttp({
          url: filter.url,
          urlParams: {
            // limit: 10000, TODO: PVP prüfen
            ...FILTER_URL_PARAMS,
          },
          apiSaveId: filter.apiSaveId,
        }));
        FILTERS.push(filter);
      }
    });

    if (PROMISES.length) {
      Promise.all(PROMISES).then(
        responses => {
          const LIST_ALL = [];

          forEach(responses, (response, index) => {
            let list;
            if (FILTERS?.[index]?.combineCopies) {
              list = onCombineCopiesData({ list: response, filter: FILTERS[index] });
            } else {
              list = response;
            }
            LIST_ALL.push(list);

            const CURRENT_FILTER_OBJECT_LOCAL = filterObjectLocal.value[FILTERS[index].id];
            CURRENT_FILTER_OBJECT_LOCAL.data = list;
            CURRENT_FILTER_OBJECT_LOCAL.loading = false;
          });

          checkFilterModelWithModelId({
            filters: FILTERS,
            listAll: LIST_ALL,
          });
        }
      );
    }
  };

  const loadDataForFiltersInGroup = ({ filtersGroup, filtersGroupKey }) => {
    if (filtersGroupKey === "undefined") { // groupBy gruppiert alle Elemente ohne Gruppe in "undefined"
      forEach(filtersGroup, filter => {
        if (filter.url && !filter.searchApi && !filter.data) {
          loadDataForCurrentFilter({ filter });
        }
      });
    } else { // für modelId
      loadDataForFiltersWithModelId({ filtersGroup });
    }
  };

  const loadDataForFilters = () => {
    if (isSimpleTable.value) {
      return;
    }

    const FILTERS_GROUP_BY_MODEL_ID = groupBy(filtersPropsAndFromAPI.value, "modelId");

    forEach(FILTERS_GROUP_BY_MODEL_ID, (filtersGroup, filtersGroupKey) => {
      loadDataForFiltersInGroup({ filtersGroup, filtersGroupKey });
    });

    forEach(filtersPropsAndFromAPI.value, filter => {
      if (filter.children?.length) {
        forEach(filter.children, filterChild => {
          if (filterChild.url && !filter.searchApi && !filterChild.data) {
            loadDataForCurrentFilter({ filter: filterChild });
          }
        });
      }

      if (filter.event) {
        setEventBusForFilterForUpdateData(filter);
      }
    });
  };

  const initFilterModelWithModelId = () => {
    if (isSimpleTable.value) {
      return;
    }

    const FILTERS_GROUP_BY_MODEL_ID = groupBy(filtersPropsAndFromAPI.value, "modelId");
    forEach(FILTERS_GROUP_BY_MODEL_ID, (filtersGroup, filtersGroupKey) => {
      if (filtersGroupKey !== "undefined") { // groupBy gruppiert alle Elemente ohne Gruppe in "undefined"
        const FILTERS = [];
        const LIST_ALL = [];
        forEach(filtersGroup, filter => {
          if (!filter.url && filter.data) {
            FILTERS.push(filter);
            LIST_ALL.push(filter.data);
          }
        });

        checkFilterModelWithModelId({
          filters: FILTERS,
          listAll: LIST_ALL,
        });
      }
    });
  };

  const destroyEventBussesForFilters = () => {
    forEach(eventBussesForFilter.value, (item, key) => {
      EventBus.$off(key);
    });
  };

  return {
    changeModelFiltersLocalFromBadRequest,
    destroyEventBussesForFilters,
    excludeRenderAttributesForFilters,
    filtersKeyById,
    filtersLocal,
    filtersLocalGrouped,
    filtersRef,
    filtersVisible,
    initFilterModelAndSortingFromLocalStorage,
    initFilterModelFromDefaultConfig,
    initFilterModelFromUrl,
    initFilterModelWithModelId,
    loadDataForFilters,
    readyFilters,
    updateAppliedModelFiltersLocal,
    updateFiltersFromAPI,
    updateFiltersVisible,
    updateMainModel,
    updateUnappliedModelFiltersLocal,
  };
}
